import type { SchemaPathTree } from '@angular/forms/signals';
import { minLength, required } from '@angular/forms/signals';
import type { Company, CompanyForm } from '@indutrust/shared-model';
import { noWhitespaceValidator } from '@indutrust/shared-util';

/**
 * Returns an initial {@link CompanyForm} value, optionally seeded from an
 * existing {@link Company}.
 *
 * Used as the initial value of the company edit form's data signal.
 */
export function createCompanyFormValue(
  company?: Partial<Company>
): CompanyForm {
  return {
    id: company?.id ?? 0,
    name: company?.name ?? '',
    status: company?.status ?? 'free',
  };
}

/**
 * Schema fragment applying the company edit form's validation rules.
 *
 * Requires a non-empty, non-whitespace `name` of at least 2 characters.
 */
export function companyFormSchema(p: SchemaPathTree<CompanyForm>): void {
  required(p.name);
  minLength(p.name, 2);
  noWhitespaceValidator(p.name);
}

/**
 * Serializes a {@link CompanyForm} into a partial payload for the PATCH API.
 *
 * Omits `status` when the caller is not an admin — only admins may change
 * the license tier.
 */
export function getCompanyEditPayload(
  value: CompanyForm,
  isAdmin: boolean
): Partial<Company> {
  return isAdmin
    ? { name: value.name, status: value.status }
    : { name: value.name };
}
